<template>
  <section>
    <form method="post" novalidate>
      <form-wrapper :validator="$v.form">
        <form-group
          horizontal
          name="type"
          attribute="attributes.product_type"
          :label="$t('attributes.product_type')"
        >
          <b-select
            v-model="form.type"
            name="type"
            :placeholder="
              $t('message.please_select') + $t('attributes.product_type')
            "
            expanded
          >
            <option value="" disabled>
              {{ $t('message.please_select') + $t('attributes.product_type') }}
            </option>
            <option
              v-for="(type, idx) in productTypes"
              :key="idx"
              :value="type.value"
            >
              {{ type.name }}
            </option>
          </b-select>
        </form-group>

        <form-group
          horizontal
          name="area_code"
          attribute="attributes.area"
          :label="$t('attributes.area')"
        >
          <b-select
            v-model="selectedArea"
            name="area_code"
            :loading="loading"
            :placeholder="$t('message.please_select') + $t('attributes.area')"
            expanded
            @input="onSelectArea"
          >
            <option value="" disabled>
              {{ $t('message.please_select') + $t('attributes.area') }}
            </option>
            <option v-for="option in areas" :key="option.code" :value="option">
              {{ option.name }}
            </option>
          </b-select>
        </form-group>

        <form-group
          horizontal
          name="catalogs"
          attribute="attributes.catalog"
          :label="$t('attributes.catalog')"
        >
          <div
            class="columns is-multiline is-mobile"
            style="padding-top: 0.4em; min-height: 300px;"
          >
            <b-checkbox
              v-for="catalog in catalogs"
              v-model="form.catalogs"
              :key="catalog.catalog_no"
              :native-value="catalog.catalog_no"
              size="is-small"
              class="column is-6 is-marginless"
            >
              [{{ catalog.catalog_no }}] {{ catalog.name }}
            </b-checkbox>
          </div>
        </form-group>

        <form-group horizontal name="days" :label="$t('attributes.days')">
          <b-select
            v-model.number="form.days"
            name="days"
            :placeholder="$t('message.please_select') + $t('attributes.days')"
          >
            <option value="" disabled>
              {{ $t('message.please_select') + $t('attributes.days') }}
            </option>
            <option v-for="option in days" :key="option" :value="option">
              {{ option }}
            </option>
          </b-select>
        </form-group>

        <form-group
          horizontal
          name="code"
          attribute="attributes.product_code"
          :label="$t('attributes.product_code')"
        >
          <b-field>
            <p class="control">
              <span class="button is-static">
                {{ form.area_code || '?' }}
              </span>
            </p>
            <b-input
              v-model="form.code"
              name="code"
              maxlength="5"
              :has-counter="false"
              :placeholder="
                $t('message.please_input') + $t('attributes.product_code')
              "
              @input="form.code = form.code.toUpperCase()"
              expanded
            ></b-input>
            <p class="control">
              <span class="button is-static">
                {{ form.days ? form.days.toString().padStart(2, 0) : '??' }}
              </span>
            </p>
          </b-field>
        </form-group>

        <form-group
          horizontal
          name="name"
          attribute="attributes.name"
          :label="$t('attributes.name')"
        >
          <b-input
            v-model="form.name"
            name="name"
            :placeholder="$t('message.please_input') + $t('attributes.name')"
            expanded
          ></b-input>
        </form-group>

        <form-group horizontal name="deposit" :label="$t('attributes.deposit')">
          <b-input
            v-model.number="form.deposit"
            name="deposit"
            type="number"
            min="0"
            :placeholder="$t('message.please_input') + $t('attributes.deposit')"
          ></b-input>
        </form-group>

        <form-group
          horizontal
          name="picture"
          attribute="attributes.product_cover"
          :label="$t('attributes.product_cover')"
        >
          <b-field class="file">
            <a
              class="button is-primary"
              @click="onSelectCoverImage"
              :disabled="!this.selectedArea"
            >
              <b-icon icon="upload"></b-icon>
              <span>{{ $t('message.please_select_file') }}</span>
            </a>
            <p
              v-if="form.picture"
              class="tag is-warning"
              style="margin-left: 4px; height: 40px"
            >
              <a
                :href="productImageUrl(form.picture)"
                @click.prevent="onClickImage(form.picture)"
              >
                {{ form.picture }}
              </a>
            </p>
          </b-field>
        </form-group>
      </form-wrapper>
    </form>

    <b-modal :active.sync="isImageModalActive">
      <p class="image">
        <img :src="productImageUrl(selectedImageUrl)" alt="" />
      </p>
    </b-modal>
  </section>
</template>

<script>
import {
  required,
  minLength,
  maxLength,
  numeric,
  minValue
} from 'vuelidate/lib/validators'
import { AREA_API } from '@/utils/apis'
import { OpenProductCoverFinder } from '@/utils/ckfinder'
import notifyService from '@/services/notifyService'

export default {
  name: 'CreateForm',

  props: {
    isSending: {
      type: Boolean,
      default: false
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    const productTypes = this.GetProductTypes()

    return {
      productTypes,
      areas: [],
      catalogs: [],
      selectedArea: null,
      days: [...Array(30).keys()].map(i => i + 1),

      form: {
        type: null,
        area_code: null,
        catalogs: [],
        days: null,
        code: '',
        name: '',
        deposit: 0,
        picture: null
      },

      isImageModalActive: false,
      selectedImageUrl: null
    }
  },
  validations: {
    form: {
      type: { required },
      area_code: { required },
      catalogs: { required },
      days: { required, numeric },
      code: { required, minLength: minLength(5), maxLength: maxLength(5) },
      name: { required },
      deposit: { required, numeric, minValue: minValue(0) },
      picture: { required }
    }
  },
  mounted() {
    this.loadAreas()
  },
  methods: {
    async loadAreas() {
      try {
        const params = { active: true }
        this.$emit('update:loading', true)
        const res = await this.axios.get(AREA_API, { params })
        const areas = res.data.data
        this.areas = areas.sort(a => a.sort)
        this.catalogs = []
        this.selectedArea = null
      } catch (e) {
        this.areas = []
        this.catalogs = []
        this.selectedArea = null

        notifyService.fetch_error('area', e)
      } finally {
        this.$emit('update:loading', false)
      }
    },
    onSelectArea(option) {
      this.catalogs = option
        ? option.catalogs.filter(c => c.status !== 0).sort(c => c.sort)
        : []
      this.form.area_code = option && option.code
      this.form.catalogs = []
    },
    async onSelectCoverImage() {
      if (!this.selectedArea) {
        return
      }

      try {
        this.form.picture = await OpenProductCoverFinder(
          this.selectedArea.image_path
        )
      } catch (e) {
        notifyService.error(e)
      }
    },
    onClickImage(url) {
      this.isImageModalActive = true
      this.selectedImageUrl = url
    },
    send() {
      this.$v.$touch()

      if (this.$v.$invalid) {
        return
      }

      this.$emit('onSend', this.form)
    }
  }
}
</script>
